/**
 * Owner Dashboard — SaaS-style overview.
 *
 * Top → bottom:
 *   1) KPI strip — 4 most important counters
 *   2) Two-column row:
 *      - 7-day stacked bar chart (SVG, no chart lib)
 *      - "งานที่ต้องทำ" (pending approvals + pending changes) as task cards
 *   3) Two-column row:
 *      - Recent activity (audit log entries)
 *      - Quick actions (sync sheet/drive, force ops)
 */
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/th';
import {
  Users,
  ListChecks,
  CheckCircle2,
  Coffee,
  AlertTriangle,
  ClipboardCheck,
  ArrowRight,
  RefreshCw,
  Cloud,
  Activity,
  TrendingUp,
} from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { SetupCard } from '../../components/SetupCard';
import { PendingApprovalsCard } from '../../components/PendingApprovalsCard';
import { NextActionCard } from '../../components/ui/NextActionCard';
import { MonthlyWorkSpine } from '../../components/MonthlyWorkSpine';
import { AttendanceTopCard } from '../../components/AttendanceTopCard';
import { auditActionLabel } from '../../lib/audit-labels';
import {
  useOwnerDashboardSummary,
  useLast7DaysStats,
  useAuditLog,
  useDriveStatus,
  useForceSheetSync,
  useForceDriveSync,
  usePendingApprovals,
} from '../../hooks';
import { useToast } from '../../components/ui/toast';
import { useConfirm } from '../../components/ui/confirm';

dayjs.locale('th');

interface AuditEntry { id: string; timestamp: string; action: string; level: string; target_id?: string }
interface DriveStatus { drive_health: string; drive_last_synced_at: string | null }
interface PendingLeave { leave_id: string; emp_name?: string; emp_code: string; leave_type: string; date: string }
interface PendingOT { ot_id: string; emp_name?: string; emp_code: string; date: string; ot_type: string }

function KpiCard({
  icon: Icon,
  label,
  value,
  hint,
  tone = 'default',
  to,
}: {
  icon: typeof Users;
  label: string;
  value: string | number;
  hint?: string;
  tone?: 'default' | 'warn' | 'danger' | 'success';
  to?: string;
}) {
  // Value stays dark; the delta/hint carries the tone colour (mockup style).
  const hintColor =
    tone === 'warn' ? 'text-amber-600'
    : tone === 'danger' ? 'text-red-600'
    : tone === 'success' ? 'text-brand-600'
    : 'text-gray-400';
  const inner = (
    <>
      <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
        <Icon className="w-4 h-4 text-brand-600 shrink-0" />
        <span className="truncate">{label}</span>
        {to && <ArrowRight className="w-4 h-4 text-gray-300 ml-auto shrink-0" />}
      </div>
      <div className="font-display text-3xl font-semibold tabular-nums text-gray-900 leading-tight">{value}</div>
      {hint && <div className={`font-display text-xs tabular-nums mt-1 ${hintColor}`}>{hint}</div>}
    </>
  );
  return to ? (
    <Link to={to} className="rounded-xl bg-white border border-gray-200 p-4 block hover:border-brand-300 transition">
      {inner}
    </Link>
  ) : (
    <div className="rounded-xl bg-white border border-gray-200 p-4">{inner}</div>
  );
}

// === 7-day bar chart (SVG, no library) ===
function MiniBarChart({ data }: { data: Array<{ date: string; checkins: number; leaves: number; ots: number }> }) {
  const max = Math.max(1, ...data.map((d) => Math.max(d.checkins, d.leaves, d.ots)));
  const w = 540;
  const h = 200;
  const padL = 30, padR = 10, padT = 10, padB = 28;
  const innerW = w - padL - padR;
  const innerH = h - padT - padB;
  const groupW = innerW / data.length;
  const barW = (groupW - 8) / 3;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" preserveAspectRatio="xMidYMid meet">
      {/* Y axis grid */}
      {[0, 0.25, 0.5, 0.75, 1].map((p, i) => {
        const y = padT + innerH - innerH * p;
        return (
          <g key={i}>
            <line x1={padL} y1={y} x2={w - padR} y2={y} stroke="#ece1e4" strokeDasharray="2 4" />
            <text x={padL - 4} y={y + 3} textAnchor="end" fontSize="9" fill="#9a938c">
              {Math.round(max * p)}
            </text>
          </g>
        );
      })}
      {data.map((d, i) => {
        const gx = padL + groupW * i + 4;
        const barH = (n: number) => (n / max) * innerH;
        const bars: Array<{ value: number; color: string; offset: number }> = [
          { value: d.checkins, color: '#5b7a4f', offset: 0 },
          { value: d.leaves, color: '#F5C973', offset: barW },
          { value: d.ots, color: '#df6fae', offset: barW * 2 },
        ];
        return (
          <g key={d.date}>
            {bars.map((b, bi) => (
              <rect
                key={bi}
                x={gx + b.offset}
                y={padT + innerH - barH(b.value)}
                width={barW}
                height={barH(b.value)}
                rx={2}
                fill={b.color}
              />
            ))}
            <text
              x={gx + (groupW - 8) / 2}
              y={h - padB + 14}
              textAnchor="middle"
              fontSize="10"
              fill="#6b6663"
            >
              {dayjs(d.date).format('D MMM')}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function ChartLegend() {
  const items = [
    { color: '#5b7a4f', label: 'เช็คอิน' },
    { color: '#F5C973', label: 'ลา' },
    { color: '#df6fae', label: 'OT' },
  ];
  return (
    <div className="flex gap-4 text-xs text-gray-600">
      {items.map((i) => (
        <div key={i.label} className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: i.color }} />
          <span>{i.label}</span>
        </div>
      ))}
    </div>
  );
}

function ActionAuditBadge({ level }: { level: string }) {
  const cls =
    level === 'ERROR' ? 'bg-red-100 text-red-700'
    : level === 'WARN' ? 'bg-amber-100 text-amber-700'
    : 'bg-blue-100 text-blue-700';
  return <span className={`badge ${cls}`}>{level}</span>;
}

export function OwnerDashboard() {
  const { data: summary } = useOwnerDashboardSummary();
  const { data: chartData } = useLast7DaysStats();
  const { data: auditData } = useAuditLog(8) as { data: { entries: AuditEntry[] } | AuditEntry[] | undefined };
  const { data: driveStatus } = useDriveStatus() as { data: DriveStatus | undefined };
  const { data: pending } = usePendingApprovals() as { data: { leaves: PendingLeave[]; ots: PendingOT[] } | undefined };
  const sheetSync = useForceSheetSync();
  const driveSync = useForceDriveSync();
  const toast = useToast();
  const confirm = useConfirm();

  const auditEntries = Array.isArray(auditData) ? auditData : auditData?.entries ?? [];

  // Build task list from pending approvals + pending changes (top 5)
  const tasks: Array<{ id: string; title: string; sub: string; to: string }> = [];
  for (const l of (pending?.leaves ?? []).slice(0, 3)) {
    tasks.push({
      id: l.leave_id,
      title: `${l.emp_name ?? l.emp_code} · ${l.leave_type}`,
      sub: dayjs(l.date).format('D MMM YYYY'),
      to: '/approvals',
    });
  }
  for (const o of (pending?.ots ?? []).slice(0, 3)) {
    tasks.push({
      id: o.ot_id,
      title: `${o.emp_name ?? o.emp_code} · OT`,
      sub: dayjs(o.date).format('D MMM YYYY'),
      to: '/approvals',
    });
  }
  if (summary && summary.pending_changes > 0) {
    tasks.push({
      id: 'pending-changes',
      title: 'HR เสนอเปลี่ยนข้อมูล',
      sub: `${summary.pending_changes} รายการรอตัดสินใจ`,
      to: '/owner/pending-changes',
    });
  }

  async function handleSync(fn: () => Promise<unknown>, label: string) {
    if (!(await confirm({ title: `เรียก ${label}?` }))) return;
    try {
      await fn();
      toast.success(`${label} เริ่มทำงานแล้ว`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  return (
    <AdminLayout
      title="ภาพรวมระบบ"
      subtitle={dayjs().format('dddd D MMMM YYYY')}
      hideCloseBanner
      actions={
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span
            className={`badge ${
              driveStatus?.drive_health === 'ok'
                ? 'badge-success'
                : driveStatus?.drive_health
                  ? 'badge-danger'
                  : 'badge-warn'
            }`}
          >
            Drive · {driveStatus?.drive_health === 'ok' ? 'ปกติ' : (driveStatus?.drive_health ?? '–')}
          </span>
        </div>
      }
    >
      {/* First-run setup helper (A17) — hides once setup is complete */}
      <div className="mb-6">
        <SetupCard />
      </div>

      {/* คำขอรออนุมัติจาก HR (HR Delegation Phase 3) — hides when empty */}
      <div className="mb-6">
        <PendingApprovalsCard />
      </div>

      {/* ภาพรวมงานเดือนนี้ — วงจรทำเงินเดือน + ขั้นที่อยู่ + งานถัดไป (Non-HR UX §2) */}
      <MonthlyWorkSpine />

      {/* สิ่งที่ต้องทำตอนนี้ — surfaces what must be cleared before running payroll (Non-HR UX §2) */}
      {summary && (summary.pending_approvals > 0 || summary.pending_changes > 0 || summary.pending_escalations > 0) && (
        <div className="mb-6">
          <NextActionCard
            icon={AlertTriangle}
            tone="warn"
            title="มีรายการค้างก่อนทำเงินเดือน"
            detail={
              <>
                รออนุมัติ {summary.pending_approvals} · คำขอแก้ไข {summary.pending_changes}
                {summary.pending_escalations > 0 && ` · เร่งด่วน ${summary.pending_escalations}`}
              </>
            }
            action={{ label: 'ไปทำเงินเดือน', to: '/owner/cycle#payroll' }}
          />
        </div>
      )}

      {/* === KPI strip === */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <KpiCard
          icon={Users}
          label="พนักงานที่ใช้งาน"
          value={summary?.employees_active ?? '—'}
          hint="คนที่ทำงานอยู่"
        />
        <KpiCard
          icon={ListChecks}
          label="รออนุมัติ"
          value={summary?.pending_approvals ?? '—'}
          hint={`ลา ${summary?.pending_leaves ?? 0} · OT ${summary?.pending_ots ?? 0}`}
          tone={summary && summary.pending_approvals > 0 ? 'warn' : 'success'}
          to="/approvals"
        />
        <KpiCard
          icon={CheckCircle2}
          label="เช็คอินวันนี้"
          value={summary?.today_checkins ?? '—'}
          hint="ครั้งแรกของวัน"
          tone="success"
        />
        <KpiCard
          icon={Coffee}
          label="ลาวันนี้"
          value={summary?.today_leaves ?? '—'}
          tone={summary && summary.today_leaves > 0 ? 'warn' : 'default'}
        />
      </div>

      {/* === Secondary stats === */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        <KpiCard
          icon={AlertTriangle}
          label="เรื่องเร่งด่วนรอตรวจ"
          value={summary?.pending_escalations ?? '—'}
          tone={summary && summary.pending_escalations > 0 ? 'danger' : 'success'}
          to="/escalations"
        />
        <KpiCard
          icon={ClipboardCheck}
          label="HR เสนอเปลี่ยนรอ Owner"
          value={summary?.pending_changes ?? '—'}
          tone={summary && summary.pending_changes > 0 ? 'warn' : 'success'}
          to="/owner/pending-changes"
        />
        <KpiCard
          icon={TrendingUp}
          label="OT สัปดาห์นี้"
          value={chartData ? chartData.reduce((s, d) => s + d.ots, 0) : '—'}
          hint="รวม 7 วันย้อนหลัง"
        />
      </div>

      {/* === Chart + Tasks === */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-6">
        <div className="lg:col-span-3 card p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-semibold">กิจกรรม 7 วันย้อนหลัง</div>
              <div className="text-xs text-gray-500">เช็คอิน · ลา · OT</div>
            </div>
            <ChartLegend />
          </div>
          {chartData && chartData.length > 0 ? (
            <MiniBarChart data={chartData} />
          ) : (
            <div className="text-center text-gray-400 text-sm py-12">ยังไม่มีข้อมูล</div>
          )}
        </div>

        <div className="lg:col-span-2 card p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="font-semibold">งานที่ต้องทำ</div>
            <Link to="/approvals" className="text-xs text-brand-700 hover:underline">
              ทั้งหมด →
            </Link>
          </div>
          {tasks.length === 0 ? (
            <div className="flex items-center justify-center gap-1.5 text-gray-400 text-sm py-8">
              <CheckCircle2 className="w-4 h-4" /> ไม่มีงานค้าง
            </div>
          ) : (
            <div className="space-y-2">
              {tasks.slice(0, 6).map((t) => (
                <Link
                  key={t.id}
                  to={t.to}
                  className="flex items-center gap-3 p-2 -mx-1 rounded-lg hover:bg-gray-50 group"
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{t.title}</div>
                    <div className="text-xs text-gray-500">{t.sub}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-brand-600" />
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* === Activity feed + Quick actions === */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 card p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-brand-600" />
              <div className="font-semibold">กิจกรรมล่าสุด</div>
            </div>
            <Link to="/owner/audit" className="text-xs text-brand-700 hover:underline">
              Audit Log →
            </Link>
          </div>
          {auditEntries.length === 0 ? (
            <div className="text-center text-gray-400 text-sm py-8">ยังไม่มีกิจกรรม</div>
          ) : (
            <div className="space-y-1">
              {auditEntries.slice(0, 8).map((e) => (
                <div
                  key={e.id}
                  className="flex items-center gap-3 py-1.5 px-1 border-b border-gray-50 last:border-0"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate">{auditActionLabel(e.action)}</div>
                    <div className="text-xs text-gray-500">
                      {dayjs(e.timestamp).format('D MMM HH:mm')}
                      {e.target_id ? ` · ${e.target_id}` : ''}
                    </div>
                  </div>
                  <ActionAuditBadge level={e.level} />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-2 space-y-3">
          <div className="card p-4">
            <div className="font-semibold mb-3">การซิงค์ด้วยตนเอง</div>
            <div className="space-y-2">
              <button
                onClick={() => handleSync(() => sheetSync.mutateAsync(undefined), 'ซิงก์ Google Sheet')}
                disabled={sheetSync.isPending}
                className="btn-secondary w-full text-sm"
              >
                <RefreshCw className="w-4 h-4" /> ซิงก์ Google Sheet
              </button>
              <button
                onClick={() => handleSync(() => driveSync.mutateAsync(undefined), 'ซิงก์ Google Drive')}
                disabled={driveSync.isPending}
                className="btn-secondary w-full text-sm"
              >
                <Cloud className="w-4 h-4" /> ซิงก์ Google Drive
              </button>
            </div>
            {driveStatus?.drive_last_synced_at && (
              <div className="text-xs text-gray-500 mt-3">
                Drive sync ล่าสุด: {dayjs(driveStatus.drive_last_synced_at).format('D MMM HH:mm')}
              </div>
            )}
          </div>

          <div className="card p-4">
            <div className="font-semibold mb-3">ลัด</div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <Link to="/owner/cycle#reconcile" className="btn-secondary py-2">
                สรุปเวลา
              </Link>
              <Link to="/owner/cycle#payroll" className="btn-secondary py-2">
                ทำเงินเดือน
              </Link>
              <Link to="/owner/onboard" className="btn-secondary py-2">
                เพิ่มพนักงาน
              </Link>
              <Link to="/owner/settings" className="btn-secondary py-2">
                ตั้งค่า
              </Link>
              <Link to="/owner/ai" className="btn-secondary py-2">
                ผู้ช่วย AI
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Top 5 มาสาย / ลางาน ของงวด — ไว้ล่างสุด */}
      <div className="mt-4">
        <AttendanceTopCard />
      </div>
    </AdminLayout>
  );
}
